import { useCallback, useState } from 'react';

/**
 * Persists a single typed setting to localStorage.
 *
 * **NOTE: This hook is NOT reactive across components.**
 * Each component using this hook maintains its own independent state.
 * Changes made in one component will NOT trigger re-renders in other components
 * using the same storageKey. For shared reactive state, use React Context instead.
 *
 * @param storageKey   - localStorage key to read/write
 * @param defaultValue - value used when nothing is stored yet
 * @returns [value, setValue] — same shape as useState, but changes are persisted
 */
export function usePersistedSetting<T>(storageKey: string, defaultValue: T): [T, (value: T | ((prev: T) => T)) => void] {
	const [value, setValueState] = useState<T>(() => {
		try {
			const stored = localStorage.getItem(storageKey);
			if (stored !== null) return JSON.parse(stored) as T;
		} catch { }
		return defaultValue;
	});

	const setValue = useCallback(
		(next: T | ((prev: T) => T)) => {
			setValueState((prev) => {
				const resolved = typeof next === 'function' ? (next as (prev: T) => T)(prev) : next;
				try {
					localStorage.setItem(storageKey, JSON.stringify(resolved));
				} catch { }
				return resolved;
			});
		},
		[storageKey],
	);

	return [value, setValue];
}
